import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Alert, Modal, SafeAreaView, ActivityIndicator, Platform, Switch } from 'react-native';
import AppTextInput from '../components/AppTextInput';
import { AuthManager, BusinessOwner } from '../utils/authManager';
import SimpleStorage from '../utils/simpleStorage';
import { SERVICE_TEMPLATES, getServiceTemplateByIndustry, createServicesFromTemplate } from '../utils/serviceTemplates';
import templateService from '../services/templateService';
import { getWidgetFeaturedUsage, MAX_WIDGET_FEATURED_SLOTS } from '../utils/widgetFeaturedLimits';
import {
  formatServicePriceShort,
  hasListedServicePrice,
  normalizeStoredServicePrice,
} from '../utils/servicePriceDisplay';

interface ServiceOption {
  name: string; // e.g., "Size", "Style", "Add-on"
  value: string; // e.g., "Large", "Premium", "Yes"
}

interface Service {
  id: string;
  name: string;
  description: string;
  /** null = price on request */
  price: number | null;
  duration: number; // in minutes
  category: string;
  subcategory?: string; // e.g., 'Men', 'Women', 'Basic', 'Premium'
  isActive: boolean;
  /** Shown on embedded website widget (max 6 combined with products). */
  featuredOnWidget?: boolean;
  requirements: string[];
  icon: string;
  options?: ServiceOption[]; // Up to 3 customizable options
}

interface ServiceManagementScreenProps {
  onBack: () => void;
}

const ServiceManagementScreen: React.FC<ServiceManagementScreenProps> = ({ onBack }) => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCSVImportModal, setShowCSVImportModal] = useState(false);
  const [csvFileName, setCsvFileName] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [businessOwner, setBusinessOwner] = useState<BusinessOwner | null>(null);
  const [serviceOptions, setServiceOptions] = useState<ServiceOption[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    duration: '',
    category: '',
    subcategory: '',
    requirements: '',
    icon: '💇‍♀️',
  });

  const categories = [
    'Hair & Beauty',
    'Health & Wellness',
    'Automotive',
    'Home Services',
    'Professional Services',
    'Halal Services',
    'Retail & Products',
    'Other'
  ];

  const serviceIcons = [
    '💇‍♀️', '💅', '✨', '🚗', '🏠', '💼', '🎨', '🔧', '📱', '💻',
    '🎵', '📚', '🏃‍♀️', '🧘‍♀️', '🍽️', '🎭', '📷', '🎪', '🏆', '⭐',
    '🥩', '🍗', '🍖', '🥓', '🦃', '🐟', '🦐', '🦀', '🧀', '🥚',
    '🍞', '🥖', '🥨', '🧈', '🥛', '🍯', '🧂', '🌶️', '🧄', '🧅',
    '🛒', '📦', '🏪', '💰', '⚖️', '📏', '🌡️', '🔬', '📋', '✅'
  ];

  // Map business industry to service category
  const mapIndustryToServiceCategory = (industry: string): string => {
    if (!industry) return 'Other';
    
    const industryLower = industry.toLowerCase().trim();
    
    // Map industry to service category
    if (industryLower.includes('hair') || industryLower.includes('salon') || industryLower.includes('beauty') || industryLower.includes('barber') || industryLower.includes('nail')) {
      return 'Hair & Beauty';
    }
    if (industryLower.includes('spa') || industryLower.includes('wellness') || industryLower.includes('massage') || industryLower.includes('therapy') || industryLower.includes('health')) {
      return 'Health & Wellness';
    }
    if (industryLower.includes('automotive') || industryLower.includes('car') || industryLower.includes('auto') || industryLower.includes('vehicle')) {
      return 'Automotive';
    }
    if (industryLower.includes('home') || industryLower.includes('cleaning') || industryLower.includes('plumbing') || industryLower.includes('electrical') || industryLower.includes('repair')) {
      return 'Home Services';
    }
    if (industryLower.includes('legal') || industryLower.includes('consulting') || industryLower.includes('professional') || industryLower.includes('accounting') || industryLower.includes('tax') || industryLower.includes('finance')) {
      return 'Professional Services';
    }
    if (industryLower.includes('halal') || industryLower.includes('islamic') || industryLower.includes('meat') || industryLower.includes('butcher')) {
      return 'Halal Services';
    }
    if (industryLower.includes('retail') || industryLower.includes('shop') || industryLower.includes('store') || industryLower.includes('grocery')) {
      return 'Retail & Products';
    }
    
    // Default
    return 'Other';
  };

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      setLoading(true);
      const owner = await AuthManager.getSavedBusinessOwner();
      if (owner) {
        setBusinessOwner(owner);
        
        // Try to load from Firestore first (most up-to-date)
        let loadedServices: Service[] = [];
        let loadedFromFirestore = false;
        
        if (Platform.OS === 'web') {
          try {
            // Use require() to ensure same module instance as unifiedAuthService
            const { doc, getDoc } = require('firebase/firestore');
            const { getWebDb, initializeFirebase } = require('../services/unifiedAuthService');

            // Ensure Firebase is initialized
            const initialized = initializeFirebase();
            if (!initialized) {
              console.warn('⚠️  ServiceManagementScreen: Firebase not initialized, skipping Firestore load');
            } else {
              const firestoreDb = getWebDb();
              if (!firestoreDb) {
                console.warn('⚠️  ServiceManagementScreen: No Firebase database instance found, skipping Firestore load');
              } else {
                // Validate Firestore instance
                if (typeof firestoreDb !== 'object' || firestoreDb === null) {
                  console.warn('⚠️  ServiceManagementScreen: Invalid Firestore instance, skipping Firestore load');
                } else {
                  // Use same pattern as productManagementService: doc(db, 'businesses', businessOwnerId, 'services', 'all')
                  const servicesDocRef = doc(firestoreDb, 'businesses', owner.id, 'services', 'all');
                  const servicesDoc = await getDoc(servicesDocRef);
                  if (servicesDoc.exists()) {
                    const data = servicesDoc.data();
                    loadedServices = data.services || [];
                    loadedFromFirestore = true;
                    console.log('✅ ServiceManagementScreen: Services loaded from Firestore');
                    
                    // Sync to local storage for offline access
                    await SimpleStorage.setItem(`services_${owner.id}`, JSON.stringify(loadedServices));
                  } else {
                    console.warn(`⚠️  ServiceManagementScreen: Services document does not exist in Firestore for business ${owner.id}`);
                  }
                }
              }
            }
          } catch (firestoreError: any) {
            console.warn('⚠️  ServiceManagementScreen: Could not load services from Firestore, trying local storage:', firestoreError);
            console.warn('⚠️  Error message:', firestoreError?.message);
            console.warn('⚠️  Error code:', firestoreError?.code);
          }
        } else {
          // Mobile: load from Firestore first (same DB as web)
          try {
            const { getMobileFirestore, initializeFirebase } = require('../services/unifiedAuthService');
            initializeFirebase();
            const getFirestore = getMobileFirestore?.();
            if (getFirestore && typeof getFirestore === 'function') {
              const db = getFirestore();
              if (db && typeof db.collection === 'function' && owner.id) {
                const servicesDoc = await db
                  .collection('businesses')
                  .doc(owner.id)
                  .collection('services')
                  .doc('all')
                  .get();
                if (servicesDoc.exists) {
                  const data = servicesDoc.data() || {};
                  loadedServices = data.services || [];
                  loadedFromFirestore = true;
                  console.log('✅ ServiceManagementScreen: Services loaded from Firestore (mobile)');
                  await SimpleStorage.setItem(`services_${owner.id}`, JSON.stringify(loadedServices));
                }
              }
            }
          } catch (firestoreError: any) {
            console.warn('⚠️  ServiceManagementScreen: Could not load services from Firestore on mobile:', firestoreError?.message);
          }
        }
        
        // Fallback to local storage if Firestore didn't work
        if (!loadedFromFirestore) {
        const savedServices = await SimpleStorage.getItem(`services_${owner.id}`);
        if (savedServices) {
            loadedServices = JSON.parse(savedServices);
            console.log('✅ Services loaded from local storage');
          }
        }
        
        if (loadedServices.length > 0) {
          setServices(
            loadedServices.map((s: Service) => ({
              ...s,
              price: normalizeStoredServicePrice((s as any).price),
            }))
          );
        } else {
          // Load default services for new business owners
          const defaultServices = getDefaultServices(owner.industry);
          setServices(defaultServices);
          await saveServices(defaultServices, owner.id);
        }
      }
    } catch (error) {
      console.error('Error loading services:', error);
      Alert.alert('Error', 'Failed to load services. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getDefaultServices = (industry: string): Service[] => {
    const baseServices: Service[] = [
      {
        id: '1',
        name: 'Basic Consultation',
        description: 'Initial consultation and assessment',
        price: 25,
        duration: 30,
        category: 'Consultation',
        isActive: true,
        requirements: ['Arrive 10 minutes early'],
        icon: '💬',
      },
    ];

    switch (industry?.toLowerCase()) {
      case 'hair salon':
      case 'beauty':
        return [
          ...baseServices,
          {
            id: '2',
            name: 'Haircut & Styling',
            description: 'Professional haircut and styling',
            price: 45,
            duration: 60,
            category: 'Hair & Beauty',
            isActive: true,
            requirements: ['Clean hair', 'No styling products'],
            icon: '💇‍♀️',
          },
          {
            id: '3',
            name: 'Hair Color',
            description: 'Full hair coloring service',
            price: 85,
            duration: 120,
            category: 'Hair & Beauty',
            isActive: true,
            requirements: ['Clean hair', 'No previous color', 'Patch test required'],
            icon: '🎨',
          },
          {
            id: '4',
            name: 'Manicure',
            description: 'Professional nail care and polish',
            price: 35,
            duration: 45,
            category: 'Hair & Beauty',
            isActive: true,
            requirements: ['Clean hands', 'No nail polish'],
            icon: '💅',
          },
        ];
      case 'spa':
      case 'wellness':
        return [
          ...baseServices,
          {
            id: '2',
            name: 'Facial Treatment',
            description: 'Deep cleansing and moisturizing facial',
            price: 75,
            duration: 90,
            category: 'Health & Wellness',
            isActive: true,
            requirements: ['Clean face', 'No makeup', 'Arrive 10 minutes early'],
            icon: '✨',
          },
          {
            id: '3',
            name: 'Massage Therapy',
            description: 'Relaxing full-body massage',
            price: 95,
            duration: 60,
            category: 'Health & Wellness',
            isActive: true,
            requirements: ['Clean body', 'Comfortable clothing'],
            icon: '🧘‍♀️',
          },
        ];
      case 'automotive':
        return [
          ...baseServices,
          {
            id: '2',
            name: 'Oil Change',
            description: 'Complete oil change service',
            price: 35,
            duration: 30,
            category: 'Automotive',
            isActive: true,
            requirements: ['Vehicle registration', 'Service history'],
            icon: '🚗',
          },
          {
            id: '3',
            name: 'Brake Inspection',
            description: 'Comprehensive brake system check',
            price: 65,
            duration: 45,
            category: 'Automotive',
            isActive: true,
            requirements: ['Vehicle registration', 'Service history'],
            icon: '🔧',
          },
        ];
      case 'halal':
      case 'butcher':
      case 'meat':
        return [
          ...baseServices,
          {
            id: '2',
            name: 'Halal Meat Cutting',
            description: 'Professional halal meat cutting and preparation',
            price: 15,
            duration: 30,
            category: 'Halal Services',
            isActive: true,
            requirements: ['Halal certification', 'Fresh meat only', 'Clean workspace'],
            icon: '🥩',
          },
          {
            id: '3',
            name: 'Custom Meat Orders',
            description: 'Custom cuts and portions of halal meat',
            price: 25,
            duration: 45,
            category: 'Halal Services',
            isActive: true,
            requirements: ['Advance order required', 'Halal certified', 'Fresh preparation'],
            icon: '🍖',
          },
          {
            id: '4',
            name: 'Halal Meat Packaging',
            description: 'Professional packaging for halal meat products',
            price: 5,
            duration: 15,
            category: 'Halal Services',
            isActive: true,
            requirements: ['Food-grade packaging', 'Proper labeling', 'Temperature control'],
            icon: '📦',
          },
        ];
      default:
        return baseServices;
    }
  };

  const saveServices = async (servicesToSave: Service[], businessOwnerId: string) => {
    try {
      // Save to local storage (for offline access)
      await SimpleStorage.setItem(`services_${businessOwnerId}`, JSON.stringify(servicesToSave));
      
      // Remove undefined values from services recursively (Firestore doesn't allow undefined)
      const removeUndefined = (obj: any): any => {
        if (obj === null || obj === undefined) {
          return null;
        }
        if (Array.isArray(obj)) {
          return obj.map(removeUndefined).filter(item => item !== undefined);
        }
        if (typeof obj === 'object') {
          const cleaned: any = {};
          Object.keys(obj).forEach(key => {
            const value = obj[key];
            if (value !== undefined) {
              cleaned[key] = removeUndefined(value);
            }
          });
          return cleaned;
        }
        return obj;
      };
      const cleanServices = servicesToSave.map(service => removeUndefined(service));
      const payload = { services: cleanServices, updatedAt: new Date().toISOString() };

      // Also save to Firestore (for cross-browser/device access)
      if (Platform.OS === 'web') {
        const { doc, setDoc } = require('firebase/firestore');
        const { getWebDb, initializeFirebase, getWebAuth } = require('../services/unifiedAuthService');

        if (!initializeFirebase()) {
          throw new Error(
            'Firebase could not be initialized in the web app. Ensure hosting env includes REACT_APP_FIREBASE_* (see FIREBASE_CONFIG_SETUP.md).'
          );
        }

        const firestoreDb = getWebDb();
        if (!firestoreDb || typeof firestoreDb !== 'object') {
          throw new Error('Firestore is not available in this browser session. Refresh the page and try again.');
        }

        // Rules require request.auth.uid == businessId for businesses/{businessId}/services/*
        const auth = getWebAuth();
        const uid = auth?.currentUser?.uid;
        if (!uid) {
          throw new Error(
            'Your browser is not signed in to Firebase (cloud sync). Log out of BookAPT, then log in again with your business email and password—the same account as the mobile app.'
          );
        }
        if (uid !== businessOwnerId) {
          throw new Error(
            'The signed-in Firebase account does not match this business profile. Log out and log in with the owner account that owns this business (IDs must match for Firestore security rules).'
          );
        }

        const servicesDocRef = doc(firestoreDb, 'businesses', businessOwnerId, 'services', 'all');
        await setDoc(servicesDocRef, payload, { merge: true });
        console.log('✅ ServiceManagementScreen: Services saved to Firestore (web)');
      } else {
        // Mobile: save to Firestore (same path as web)
        try {
          const { getMobileFirestore, initializeFirebase } = require('../services/unifiedAuthService');
          initializeFirebase();
          const getFirestore = getMobileFirestore?.();
          if (getFirestore && typeof getFirestore === 'function') {
            const db = getFirestore();
            if (db && typeof db.collection === 'function' && businessOwnerId) {
              await db
                .collection('businesses')
                .doc(businessOwnerId)
                .collection('services')
                .doc('all')
                .set(payload, { merge: true });
              console.log('✅ ServiceManagementScreen: Services saved to Firestore (mobile)');
            }
          }
        } catch (firestoreError: any) {
          console.warn('⚠️  ServiceManagementScreen: Could not save services to Firestore on mobile:', firestoreError?.message);
          throw firestoreError;
        }
      }
    } catch (error) {
      console.error('Error saving services:', error);
      throw error;
    }
  };

  const handleAddService = () => {
    setEditingService(null);
    setServiceOptions([]);
    
    // Auto-select category based on business owner's industry
    const owner = businessOwner || null;
    const autoCategory = owner ? mapIndustryToServiceCategory(owner.industry || '') : 'Other';
    
    setFormData({
      name: '',
      description: '',
      price: '',
      duration: '',
      category: autoCategory, // Auto-set based on business industry
      subcategory: '',
      requirements: '',
      icon: '💇‍♀️',
    });
    setShowAddModal(true);
  };

  const handleEditService = (service: Service) => {
    setEditingService(service);
    setServiceOptions(service.options || []);
    setFormData({
      name: service.name,
      description: service.description,
      price: hasListedServicePrice(service.price) ? service.price!.toString() : '',
      duration: service.duration.toString(),
      category: service.category,
      subcategory: service.subcategory || '',
      requirements: service.requirements.join(', '),
      icon: service.icon,
    });
    setShowAddModal(true);
  };

  const handleSaveService = async () => {
    try {
      if (!formData.name || !formData.duration) {
        Alert.alert('Error', 'Please fill in service name and duration');
        return;
      }

      const priceTrim = formData.price.trim();
      let price: number | null = null;
      if (priceTrim !== '') {
        const parsed = parseFloat(priceTrim);
        if (Number.isNaN(parsed) || parsed < 0) {
          Alert.alert('Error', 'Please enter a valid price, or leave price empty for “price on request”.');
          return;
        }
        price = parsed > 0 ? parsed : null;
      }

      const businessOwner = await AuthManager.getSavedBusinessOwner();
      if (!businessOwner) {
        Alert.alert('Error', 'Business owner not found');
        return;
      }

      const newService: Service = {
        id: editingService?.id || Date.now().toString(),
        name: formData.name.trim(),
        description: formData.description.trim(),
        price,
        duration: parseInt(formData.duration),
        category: formData.category,
        subcategory: formData.subcategory.trim() || undefined,
        isActive: editingService?.isActive ?? true,
        requirements: formData.requirements.split(',').map(req => req.trim()).filter(req => req),
        icon: formData.icon,
        options: serviceOptions.length > 0 ? serviceOptions : undefined,
      };

      let updatedServices;
      if (editingService) {
        updatedServices = services.map(s => s.id === editingService.id ? newService : s);
      } else {
        updatedServices = [...services, newService];
      }

      setServices(updatedServices);
      await saveServices(updatedServices, businessOwner.id);
      setShowAddModal(false);
      Alert.alert('Success', `Service ${editingService ? 'updated' : 'added'} successfully!`);
    } catch (error: any) {
      console.error('Error saving service:', error);
      const msg =
        error && typeof error.message === 'string' && error.message.length > 0
          ? error.message
          : 'Failed to save service. Please try again.';
      Alert.alert('Could not sync to cloud', msg);
    }
  };

  // Service Options Management Functions
  const handleAddServiceOption = () => {
    if (serviceOptions.length >= 3) {
      Alert.alert('Limit Reached', 'You can add up to 3 customizable options per service.');
      return;
    }
    const newOption: ServiceOption = {
      name: '',
      value: ''
    };
    setServiceOptions([...serviceOptions, newOption]);
  };

  const handleRemoveServiceOption = (index: number) => {
    const updatedOptions = serviceOptions.filter((_, i) => i !== index);
    setServiceOptions(updatedOptions);
  };

  const handleUpdateServiceOptionName = (index: number, name: string) => {
    const updatedOptions = [...serviceOptions];
    updatedOptions[index].name = name;
    setServiceOptions(updatedOptions);
  };

  const handleUpdateServiceOptionValue = (index: number, value: string) => {
    const updatedOptions = [...serviceOptions];
    updatedOptions[index].value = value;
    setServiceOptions(updatedOptions);
  };

  const handleToggleService = async (serviceId: string) => {
    try {
      const businessOwner = await AuthManager.getSavedBusinessOwner();
      if (!businessOwner) return;

      const updatedServices = services.map(service =>
        service.id === serviceId ? { ...service, isActive: !service.isActive } : service
      );

      setServices(updatedServices);
      await saveServices(updatedServices, businessOwner.id);
    } catch (error) {
      console.error('Error toggling service:', error);
      Alert.alert('Error', 'Failed to update service status');
    }
  };

  const handleToggleFeaturedWidget = async (serviceId: string) => {
    try {
      const owner = await AuthManager.getSavedBusinessOwner();
      if (!owner) return;

      const svc = services.find(s => s.id === serviceId);
      if (!svc) return;

      const next = !svc.featuredOnWidget;
      if (next) {
        const usage = await getWidgetFeaturedUsage(owner.id);
        const already = !!svc.featuredOnWidget;
        if (!already && usage.used >= MAX_WIDGET_FEATURED_SLOTS) {
          Alert.alert(
            'Website widget limit',
            `You can feature up to ${MAX_WIDGET_FEATURED_SLOTS} services and products combined on your embedded widget. Turn off another featured item in Service or Product management.`
          );
          return;
        }
      }

      const updatedServices = services.map(service =>
        service.id === serviceId ? { ...service, featuredOnWidget: next } : service
      );
      setServices(updatedServices);
      await saveServices(updatedServices, owner.id);
    } catch (error) {
      console.error('Error toggling widget feature:', error);
      Alert.alert('Error', 'Failed to update widget visibility');
    }
  };

  const handleDeleteService = async (serviceId: string) => {
    const service = services.find(s => s.id === serviceId);
    if (!service) {
      if (Platform.OS === 'web') {
        window.alert('Error: Service not found.');
      } else {
        Alert.alert('Error', 'Service not found.');
      }
      return;
    }

    const businessOwner = await AuthManager.getSavedBusinessOwner();
    if (!businessOwner) {
      if (Platform.OS === 'web') {
        window.alert('Error: Business owner not found. Please log in again.');
      } else {
        Alert.alert('Error', 'Business owner not found. Please log in again.');
      }
      return;
    }

    const confirmMessage = `Are you sure you want to delete "${service.name}"? This action cannot be undone.`;

    if (Platform.OS === 'web') {
      // Use window.confirm for web
      if (window.confirm(confirmMessage)) {
        try {
          const updatedServices = services.filter(s => s.id !== serviceId);
          setServices(updatedServices);
          await saveServices(updatedServices, businessOwner.id);
          window.alert(`"${service.name}" has been deleted successfully!`);
        } catch (error) {
          console.error('Error deleting service:', error);
          window.alert('Failed to delete service. Please try again.');
        }
      }
    } else {
      // Use Alert.alert for mobile
    Alert.alert(
      'Delete Service',
        confirmMessage,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
                const updatedServices = services.filter(s => s.id !== serviceId);
              setServices(updatedServices);
              await saveServices(updatedServices, businessOwner.id);
                Alert.alert('Success', `"${service.name}" has been deleted successfully!`);
            } catch (error) {
              console.error('Error deleting service:', error);
                Alert.alert('Error', 'Failed to delete service. Please try again.');
            }
          },
        },
      ]
    );
    }
  };

  const formatPriceDisplay = (price: number | null) => formatServicePriceShort(price);
  const formatDuration = (duration: number) => `${duration} min`;

  const handleSaveAsTemplate = async () => {
    if (!businessOwner || services.length === 0) {
      Alert.alert('Error', 'Please add services before saving as a template.');
      return;
    }

    // Prompt for template name
    if (Platform.OS === 'web') {
      const templateName = window.prompt('Enter a name for this template:', `${businessOwner.companyName || 'My'} Service Template`);
      if (!templateName) return;
      await saveTemplateWithName(templateName);
    } else {
      // For mobile, use Alert.prompt
      Alert.prompt(
        'Save as Template',
        'Enter a name for this template:',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Save',
            onPress: async (name) => {
              if (name) {
                await saveTemplateWithName(name);
              }
            }
          }
        ],
        'plain-text',
        `${businessOwner.companyName || 'My'} Service Template`
      );
    }
  };

  const saveTemplateWithName = async (templateName: string) => {
    try {
      // Convert services to template format (remove id)
      const templateServices = services.map(service => {
        const { id, ...templateService } = service;
        return templateService;
      });

      // Determine category from services
      const categories = [...new Set(services.map(s => s.category))];
      const categoryName = categories.length === 1 ? categories[0] : 'Mixed Categories';
      const icon = services[0]?.icon || '💼';

      // Save template
      await templateService.saveCustomServiceTemplate({
        name: templateName,
        categoryName,
        icon,
        services: templateServices,
        businessOwnerId: businessOwner?.id,
      });

      Alert.alert('Success', `Template "${templateName}" saved successfully! You can reuse this template later.`);
    } catch (error) {
      console.error('Error saving template:', error);
      Alert.alert('Error', 'Failed to save template. Please try again.');
    }
  };

  const handleExportTemplate = async () => {
    if (!businessOwner || services.length === 0) {
      Alert.alert('Error', 'Please add services before exporting.');
      return;
    }

    try {
      // Create CSV content from current services
      const headers = ['Name', 'Description', 'Price', 'Duration', 'Category', 'Subcategory', 'Requirements', 'Icon', 'Option 1 Name', 'Option 1 Value', 'Option 2 Name', 'Option 2 Value', 'Option 3 Name', 'Option 3 Value'];
      const rows = services.map(service => {
        const options = service.options || [];
        const option1Name = options[0]?.name || '';
        const option1Value = options[0]?.value || '';
        const option2Name = options[1]?.name || '';
        const option2Value = options[1]?.value || '';
        const option3Name = options[2]?.name || '';
        const option3Value = options[2]?.value || '';
        
        return [
          service.name,
          service.description || '',
          service.price != null ? String(service.price) : '',
          service.duration.toString(),
          service.category,
          service.subcategory || '',
          service.requirements.join('; '),
          service.icon || '💇‍♀️',
          option1Name,
          option1Value,
          option2Name,
          option2Value,
          option3Name,
          option3Value
        ];
      });

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      if (Platform.OS === 'web') {
        // Create and download CSV file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `services_export_${businessOwner.companyName || 'my_business'}_${Date.now()}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        Alert.alert('Success', 'Services exported as CSV! You can edit it in Excel/Google Sheets and import it back.');
      } else {
        // For mobile, we'd need to use a file sharing library
        Alert.alert('Info', 'CSV export is available on web. Please use the web version to export services.');
      }
    } catch (error) {
      console.error('Error exporting services:', error);
      Alert.alert('Error', 'Failed to export services. Please try again.');
    }
  };

  const handleDownloadTemplate = async () => {
    if (!businessOwner) {
      if (Platform.OS === 'web') {
        window.alert('Please log in as a business owner to download templates.');
      } else {
        Alert.alert('Error', 'Please log in as a business owner to download templates.');
      }
      return;
    }
    
    try {
      // Get template based on business owner's industry
      const template = getServiceTemplateByIndustry(businessOwner.industry || 'General');
      
      if (!template) {
        const errorMsg = `No template found for your business type (${businessOwner.industry || 'General'}).`;
        if (Platform.OS === 'web') {
          window.alert(errorMsg);
        } else {
          Alert.alert('Error', errorMsg);
        }
        return;
      }

      // Create CSV content from template
      const headers = ['Name', 'Description', 'Price', 'Duration', 'Category', 'Subcategory', 'Requirements', 'Icon', 'Option 1 Name', 'Option 1 Value', 'Option 2 Name', 'Option 2 Value', 'Option 3 Name', 'Option 3 Value'];
      const rows = template.services.map(service => {
        const options = service.options || [];
        const option1Name = options[0]?.name || '';
        const option1Value = options[0]?.value || '';
        const option2Name = options[1]?.name || '';
        const option2Value = options[1]?.value || '';
        const option3Name = options[2]?.name || '';
        const option3Value = options[2]?.value || '';
        
        return [
          service.name,
          service.description || '',
          service.price != null ? String(service.price) : '',
          service.duration.toString(),
          service.category,
          service.subcategory || '',
          service.requirements.join('; '),
          service.icon || '💇‍♀️',
          option1Name,
          option1Value,
          option2Name,
          option2Value,
          option3Name,
          option3Value
        ];
      });

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      if (Platform.OS === 'web') {
        // Create and download CSV file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `service_template_${template.businessType}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // For mobile, we'd need to use a file sharing library
        Alert.alert('Info', 'CSV download is available on web. Please use the web version to download templates.');
      }
    } catch (error) {
      console.error('Error downloading template:', error);
      if (Platform.OS === 'web') {
        window.alert('Failed to download template');
      } else {
        Alert.alert('Error', 'Failed to download template');
      }
    }
  };

  const handleCSVImport = () => {
    setShowCSVImportModal(true);
  };

  const handleCSVFileUpload = async (file: File | null) => {
    if (!file) return;
    
    try {
      setCsvFileName(file.name);
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        if (text.trim()) {
          handleCSVFileSelect(text);
        } else {
          Alert.alert('Error', 'CSV file is empty');
          setCsvFileName('');
        }
      };
      reader.onerror = () => {
        Alert.alert('Error', 'Failed to read CSV file');
        setCsvFileName('');
      };
      reader.readAsText(file);
    } catch (error) {
      console.error('Error reading file:', error);
      Alert.alert('Error', 'Failed to read CSV file');
      setCsvFileName('');
    }
  };

  const handleCSVFileSelect = async (csvContent: string) => {
    try {
      const businessOwner = await AuthManager.getSavedBusinessOwner();
      if (!businessOwner) {
        Alert.alert('Error', 'Business owner not found');
        return;
      }

      const lines = csvContent.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        Alert.alert('Error', 'CSV file is empty or invalid');
        return;
      }
      
      // Parse header
      const header = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      const nameIndex = header.findIndex(h => h.toLowerCase().includes('name') && !h.toLowerCase().includes('option'));
      const priceIndex = header.findIndex(h => h.toLowerCase().includes('price'));
      const durationIndex = header.findIndex(h => h.toLowerCase().includes('duration'));
      const categoryIndex = header.findIndex(h => h.toLowerCase() === 'category');
      const subcategoryIndex = header.findIndex(h => h.toLowerCase() === 'subcategory');
      
      if (nameIndex === -1 || durationIndex === -1 || categoryIndex === -1) {
        Alert.alert('Error', 'CSV must contain: Name, Duration, Category (Price column optional; leave blank for price on request)');
        return;
      }
      
      // Parse services
      const newServices: Service[] = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
        if (values[nameIndex] && values[durationIndex]) {
          // Parse options (up to 3)
          const options: ServiceOption[] = [];
          const option1NameIndex = header.findIndex(h => h.toLowerCase() === 'option 1 name');
          const option1ValueIndex = header.findIndex(h => h.toLowerCase() === 'option 1 value');
          const option2NameIndex = header.findIndex(h => h.toLowerCase() === 'option 2 name');
          const option2ValueIndex = header.findIndex(h => h.toLowerCase() === 'option 2 value');
          const option3NameIndex = header.findIndex(h => h.toLowerCase() === 'option 3 name');
          const option3ValueIndex = header.findIndex(h => h.toLowerCase() === 'option 3 value');
          
          if (option1NameIndex !== -1 && option1ValueIndex !== -1 && values[option1NameIndex] && values[option1ValueIndex]) {
            options.push({ name: values[option1NameIndex], value: values[option1ValueIndex] });
          }
          if (option2NameIndex !== -1 && option2ValueIndex !== -1 && values[option2NameIndex] && values[option2ValueIndex]) {
            options.push({ name: values[option2NameIndex], value: values[option2ValueIndex] });
          }
          if (option3NameIndex !== -1 && option3ValueIndex !== -1 && values[option3NameIndex] && values[option3ValueIndex]) {
            options.push({ name: values[option3NameIndex], value: values[option3ValueIndex] });
          }

          const rawPriceCell =
            priceIndex !== -1 && values[priceIndex] !== undefined ? String(values[priceIndex]).trim() : '';
          let price: number | null = null;
          if (rawPriceCell !== '') {
            const parsed = parseFloat(rawPriceCell);
            price = Number.isNaN(parsed) || parsed <= 0 ? null : parsed;
          }
          
          const service: Service = {
            id: `csv_${Date.now()}_${i}`,
            name: values[nameIndex],
            description: values[header.findIndex(h => h.toLowerCase().includes('description'))] || '',
            price,
            duration: parseInt(values[durationIndex]) || 30,
            category: values[categoryIndex] || categories[0],
            subcategory: subcategoryIndex !== -1 ? (values[subcategoryIndex] || undefined) : undefined,
            isActive: true,
            requirements: values[header.findIndex(h => h.toLowerCase().includes('requirement'))]?.split(';').filter(Boolean) || [],
            icon: values[header.findIndex(h => h.toLowerCase().includes('icon'))] || '💇‍♀️',
            options: options.length > 0 ? options : undefined,
          };
          newServices.push(service);
        }
      }

      if (newServices.length === 0) {
        if (Platform.OS === 'web') {
          window.alert('No valid services found in CSV file');
        } else {
          Alert.alert('Error', 'No valid services found in CSV file');
        }
        setShowCSVImportModal(false);
        setCsvFileName('');
        return;
      }

      // Close modal immediately and import services
      setShowCSVImportModal(false);
      setCsvFileName('');
      
      // Import services directly
      const updatedServices = [...services, ...newServices];
      setServices(updatedServices);
      await saveServices(updatedServices, businessOwner.id);
    } catch (error: any) {
      console.error('Error importing CSV:', error);
      Alert.alert('Error', `Failed to import CSV: ${error.message || 'Unknown error'}`);
      setCsvFileName('');
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerTopRow}>
            <TouchableOpacity style={styles.backButton} onPress={onBack}>
              <Text style={styles.backButtonText}>← Back</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.headerTitleRow}>
            <Text style={styles.headerTitle}>Service Management</Text>
            <View style={styles.placeholder} />
          </View>
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#007AFF" />
          <Text style={styles.loadingText}>Loading services...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerTopRow}>
          <TouchableOpacity style={styles.backButton} onPress={onBack}>
            <Text style={styles.backButtonText}>← Back</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.headerTitleRow}>
          <Text style={styles.headerTitle}>Service Management</Text>
          <TouchableOpacity style={styles.addButton} onPress={handleAddService}>
            <Text style={styles.addButtonText}>+ Add</Text>
          </TouchableOpacity>
        </View>
        <Text style={styles.widgetHintText}>
          Website widget: feature up to {MAX_WIDGET_FEATURED_SLOTS} services and products combined—toggle “Show on website widget” below (or in Product management).
        </Text>
      </View>

      {/* Import Buttons */}
      <View style={styles.importButtonsContainer}>
        {services.length > 0 && (
          <>
            <TouchableOpacity style={styles.importButton} onPress={handleSaveAsTemplate}>
              <Text style={styles.importButtonText}>💾 Save Template</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.importButton} onPress={handleExportTemplate}>
              <Text style={styles.importButtonText}>📤 Export CSV</Text>
            </TouchableOpacity>
          </>
        )}
        <TouchableOpacity style={styles.importButton} onPress={handleDownloadTemplate}>
          <Text style={styles.importButtonText}>📥 Download Template</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.importButton} onPress={handleCSVImport}>
          <Text style={styles.importButtonText}>📤 Import CSV</Text>
        </TouchableOpacity>
      </View>

      <ScrollView style={styles.content}>
        {services.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyIcon}>💇‍♀️</Text>
            <Text style={styles.emptyTitle}>No Services Yet</Text>
            <Text style={styles.emptyText}>
              Add your first service to start accepting bookings
            </Text>
            <TouchableOpacity style={styles.emptyButton} onPress={handleAddService}>
              <Text style={styles.emptyButtonText}>Add Service</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.servicesList}>
            {services.map((service) => (
              <View key={service.id} style={styles.serviceCard}>
                <View style={styles.serviceHeader}>
                  <View style={styles.serviceInfo}>
                    <Text style={styles.serviceIcon}>{service.icon}</Text>
                    <View style={styles.serviceDetails}>
                      <Text style={styles.serviceName}>{service.name}</Text>
                      {service.category && service.category.trim() && (
                        <Text style={styles.serviceCategory}>{service.category}</Text>
                      )}
                    </View>
                  </View>
                  <View style={styles.serviceStatus}>
                    <TouchableOpacity
                      style={[
                        styles.statusToggle,
                        service.isActive ? styles.statusActive : styles.statusInactive
                      ]}
                      onPress={() => handleToggleService(service.id)}
                    >
                      <Text style={styles.statusText}>
                        {service.isActive ? 'Active' : 'Inactive'}
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>

                <View style={styles.widgetFeatureRow}>
                  <Text style={styles.widgetFeatureLabel}>Show on website widget</Text>
                  <Switch
                    value={!!service.featuredOnWidget}
                    onValueChange={() => handleToggleFeaturedWidget(service.id)}
                    disabled={!service.isActive}
                    trackColor={{ false: '#767577', true: '#667eea' }}
                    thumbColor={service.featuredOnWidget ? '#FFFFFF' : '#f4f3f4'}
                  />
                </View>

                {service.description && service.description.trim() && (
                  <Text style={styles.serviceDescription}>{service.description}</Text>
                )}

                {service.requirements.length > 0 && (
                  <View style={styles.requirementsContainer}>
                    <Text style={styles.requirementsTitle}>Requirements:</Text>
                    {service.requirements.map((req, index) => (
                      <Text key={index} style={styles.requirementItem}>• {req}</Text>
                    ))}
                  </View>
                )}

                <View style={styles.serviceFooter}>
                  <View style={styles.serviceStats}>
                <View style={styles.serviceMeta}>
                  <View style={styles.metaItem}>
                    <Text style={styles.metaLabel}>Price</Text>
                    <Text style={styles.metaValue}>{formatPriceDisplay(service.price)}</Text>
                  </View>
                  <View style={styles.metaItem}>
                    <Text style={styles.metaLabel}>Duration</Text>
                    <Text style={styles.metaValue}>{formatDuration(service.duration)}</Text>
                  </View>
                </View>
                  </View>

                <View style={styles.serviceActions}>
                  <TouchableOpacity
                    style={styles.editButton}
                    onPress={() => handleEditService(service)}
                  >
                    <Text style={styles.editButtonText}>Edit</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.deleteButton}
                    onPress={() => handleDeleteService(service.id)}
                  >
                    <Text style={styles.deleteButtonText}>Delete</Text>
                  </TouchableOpacity>
                  </View>
                </View>
              </View>
            ))}
          </View>
        )}
      </ScrollView>

      {/* Add/Edit Service Modal */}
      <Modal
        visible={showAddModal}
        animationType="slide"
        transparent={true}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {editingService ? 'Edit Service' : 'Add New Service'}
              </Text>
              <TouchableOpacity onPress={() => setShowAddModal(false)}>
                <Text style={styles.modalCloseButton}>✕</Text>
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalBody}>
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Service Name *</Text>
                <AppTextInput
                  style={styles.input}
                  value={formData.name}
                  onChangeText={(text) => setFormData({...formData, name: text})}
                  placeholder="Enter service name"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Description</Text>
                <AppTextInput
                  style={[styles.input, styles.textArea]}
                  value={formData.description}
                  onChangeText={(text) => setFormData({...formData, description: text})}
                  placeholder="Enter service description"
                  multiline
                  numberOfLines={3}
                />
              </View>

              <View style={styles.row}>
                <View style={[styles.inputGroup, styles.halfWidth]}>
                  <Text style={styles.inputLabel}>Price ($)</Text>
                  <AppTextInput
                    style={styles.input}
                    value={formData.price}
                    onChangeText={(text) => setFormData({...formData, price: text})}
                    placeholder="Optional — leave blank for price on request"
                    keyboardType="numeric"
                  />
                </View>
                <View style={[styles.inputGroup, styles.halfWidth]}>
                  <Text style={styles.inputLabel}>Duration (min) *</Text>
                  <AppTextInput
                    style={styles.input}
                    value={formData.duration}
                    onChangeText={(text) => setFormData({...formData, duration: text})}
                    placeholder="30"
                    keyboardType="numeric"
                  />
                </View>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Category</Text>
                <View style={styles.categoryDisplayContainer}>
                  <Text style={styles.categoryDisplayText}>
                    {formData.category || 'Auto-selected based on business category'}
                        </Text>
                  <Text style={styles.categoryHelperText}>
                    Category is automatically set based on your business type
                  </Text>
                  </View>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Subcategory</Text>
                <AppTextInput
                  style={styles.input}
                  value={formData.subcategory}
                  onChangeText={(text) => setFormData({...formData, subcategory: text})}
                  placeholder="Enter subcategory (e.g., Men, Women, Basic, Premium)"
                />
              </View>

              {/* Service Options Section */}
              <View style={styles.inputGroup}>
                <View style={styles.optionsHeader}>
                  <Text style={styles.inputLabel}>Service Options (Optional)</Text>
                  {serviceOptions.length < 3 && (
                    <TouchableOpacity
                      style={styles.addOptionButton}
                      onPress={handleAddServiceOption}
                    >
                      <Text style={styles.addOptionButtonText}>+ Add Option</Text>
                    </TouchableOpacity>
                  )}
                </View>
                <Text style={styles.helperText}>
                  Add up to 3 customizable options (e.g., Size: Large, Style: Premium)
                </Text>

                {serviceOptions.map((option, optionIndex) => (
                  <View key={optionIndex} style={styles.optionCard}>
                    <View style={styles.optionHeader}>
                      <AppTextInput
                        style={[styles.input, styles.optionNameInput]}
                        value={option.name}
                        onChangeText={(text) => handleUpdateServiceOptionName(optionIndex, text)}
                        placeholder="Option name (e.g., Size, Style)"
                      />
                      <AppTextInput
                        style={[styles.input, styles.optionValueInput]}
                        value={option.value}
                        onChangeText={(text) => handleUpdateServiceOptionValue(optionIndex, text)}
                        placeholder="Value (e.g., Large, Premium)"
                      />
                      <TouchableOpacity
                        style={styles.removeOptionButton}
                        onPress={() => handleRemoveServiceOption(optionIndex)}
                      >
                        <Text style={styles.removeOptionButtonText}>✕</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                ))}
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Icon</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  <View style={styles.iconContainer}>
                    {serviceIcons.map((icon) => (
                      <TouchableOpacity
                        key={icon}
                        style={[
                          styles.iconButton,
                          formData.icon === icon && styles.iconButtonActive
                        ]}
                        onPress={() => setFormData({...formData, icon})}
                      >
                        <Text style={styles.iconText}>{icon}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </ScrollView>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Requirements (comma-separated)</Text>
                <AppTextInput
                  style={[styles.input, styles.textArea]}
                  value={formData.requirements}
                  onChangeText={(text) => setFormData({...formData, requirements: text})}
                  placeholder="e.g., Clean hair, No styling products, Arrive 10 minutes early"
                  multiline
                  numberOfLines={2}
                />
              </View>
            </ScrollView>

            <View style={styles.modalFooter}>
              <TouchableOpacity
                style={styles.cancelModalButton}
                onPress={() => setShowAddModal(false)}
              >
                <Text style={styles.cancelModalButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.saveModalButton}
                onPress={handleSaveService}
              >
                <Text style={styles.saveModalButtonText}>
                  {editingService ? 'Update Service' : 'Add Service'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* CSV Import Modal */}
      <Modal
        visible={showCSVImportModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => {
          setShowCSVImportModal(false);
          setCsvFileName('');
        }}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Import Services from CSV</Text>
              <TouchableOpacity onPress={() => {
                setShowCSVImportModal(false);
                setCsvFileName('');
              }}>
                <Text style={styles.modalCloseButton}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.modalBody}>
              <Text style={styles.csvInstructions}>
                Upload a CSV file to import services. Format:{'\n'}
                Name,Description,Price (optional),Duration,Category,Subcategory,Requirements,Icon,Option 1 Name,Option 1 Value,Option 2 Name,Option 2 Value,Option 3 Name,Option 3 Value
              </Text>
              {Platform.OS === 'web' && (
                <View style={styles.csvUploadContainer}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      handleCSVFileUpload(file);
                    }}
                  />
                  <TouchableOpacity
                    style={styles.csvUploadButton}
                    onPress={() => fileInputRef.current?.click()}
                  >
                    <Text style={styles.csvUploadButtonText}>
                      {csvFileName || '📁 Upload CSV File'}
                    </Text>
                  </TouchableOpacity>
                  {csvFileName ? (
                    <Text style={styles.csvFileName}>Selected: {csvFileName}</Text>
                  ) : (
                    <Text style={styles.csvFileName}>No file selected</Text>
                  )}
                </View>
              )}
            </ScrollView>
            <View style={styles.modalFooter}>
              <TouchableOpacity
                style={styles.cancelModalButton}
                onPress={() => {
                  setShowCSVImportModal(false);
                  setCsvFileName('');
                }}
              >
                <Text style={styles.cancelModalButtonText}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  header: {
    backgroundColor: '#007AFF',
    padding: 20,
    paddingTop: 50,
  },
  headerTopRow: {
    marginBottom: 12,
  },
  headerTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  backButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
  },
  backButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: 'white',
  },
  widgetHintText: {
    marginTop: 10,
    fontSize: 12,
    color: 'rgba(255,255,255,0.9)',
    lineHeight: 16,
  },
  addButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
  },
  addButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  placeholder: {
    width: 50,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyIcon: {
    fontSize: 64,
    marginBottom: 20,
  },
  emptyTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 12,
  },
  emptyText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginBottom: 30,
    lineHeight: 24,
  },
  emptyButton: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  emptyButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  servicesList: {
    gap: 16,
  },
  serviceCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  serviceHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  widgetFeatureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
    paddingVertical: 6,
    paddingHorizontal: 2,
  },
  widgetFeatureLabel: {
    fontSize: 14,
    color: '#444',
    fontWeight: '500',
    flex: 1,
    paddingRight: 8,
  },
  serviceInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  serviceIcon: {
    fontSize: 24,
    marginRight: 12,
  },
  serviceDetails: {
    flex: 1,
  },
  serviceName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  serviceCategory: {
    fontSize: 14,
    color: '#666',
  },
  serviceStatus: {
    marginLeft: 12,
  },
  statusToggle: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  statusActive: {
    backgroundColor: '#34C759',
  },
  statusInactive: {
    backgroundColor: '#FF3B30',
  },
  statusText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
  },
  serviceDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 12,
    lineHeight: 20,
  },
  serviceFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  serviceStats: {
    flex: 1,
  },
  serviceMeta: {
    flexDirection: 'row',
  },
  metaItem: {
    flex: 1,
    alignItems: 'center',
  },
  metaLabel: {
    fontSize: 12,
    color: '#999',
    marginBottom: 4,
  },
  metaValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
  },
  requirementsContainer: {
    marginBottom: 12,
  },
  requirementsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 6,
  },
  requirementItem: {
    fontSize: 12,
    color: '#666',
    marginBottom: 2,
  },
  serviceActions: {
    flexDirection: 'row',
  },
  editButton: {
    backgroundColor: '#2563EB', // Professional blue
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    marginRight: 8,
  },
  editButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  deleteButton: {
    backgroundColor: '#FF3B30',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  deleteButtonText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 12,
    width: '90%',
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E1E5E9',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
  },
  modalCloseButton: {
    fontSize: 20,
    color: '#8E8E93',
  },
  modalBody: {
    padding: 20,
  },
  inputGroup: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#E1E5E9',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: '#1C1C1E',
    backgroundColor: 'white',
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  row: {
    flexDirection: 'row',
    gap: 12,
  },
  halfWidth: {
    flex: 1,
  },
  categoryContainer: {
    flexDirection: 'row',
    gap: 8,
  },
  categoryButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 16,
    backgroundColor: '#F8F9FA',
    borderWidth: 1,
    borderColor: '#E1E5E9',
  },
  categoryButtonActive: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF',
  },
  categoryButtonText: {
    fontSize: 12,
    color: '#1C1C1E',
    fontWeight: '500',
  },
  categoryButtonTextActive: {
    color: 'white',
  },
  categoryDisplayContainer: {
    padding: 12,
    backgroundColor: '#F8F8F8',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E0E0E0',
  },
  categoryDisplayText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  categoryHelperText: {
    fontSize: 12,
    color: '#666',
    fontStyle: 'italic',
  },
  // Options styles
  optionsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  addOptionButton: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  addOptionButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  helperText: {
    fontSize: 12,
    color: '#666',
    marginBottom: 12,
    fontStyle: 'italic',
  },
  optionCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  optionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  optionNameInput: {
    flex: 1,
  },
  optionValueInput: {
    flex: 1,
  },
  removeOptionButton: {
    backgroundColor: '#EF4444',
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  removeOptionButtonText: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
  },
  iconContainer: {
    flexDirection: 'row',
    gap: 8,
  },
  iconButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F8F9FA',
    borderWidth: 1,
    borderColor: '#E1E5E9',
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconButtonActive: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF',
  },
  iconText: {
    fontSize: 20,
  },
  modalFooter: {
    flexDirection: 'row',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#E1E5E9',
    gap: 12,
  },
  cancelModalButton: {
    flex: 1,
    backgroundColor: '#F8F9FA',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelModalButtonText: {
    color: '#1C1C1E',
    fontSize: 16,
    fontWeight: '600',
  },
  saveModalButton: {
    flex: 1,
    backgroundColor: '#007AFF',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  saveModalButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  // Import buttons
  importButtonsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: 12,
    gap: 8,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E1E5E9',
  },
  importButton: {
    minWidth: '47%',
    flex: 1,
    backgroundColor: '#F8F9FA',
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E1E5E9',
    alignItems: 'center',
  },
  importButtonText: {
    color: '#007AFF',
    fontSize: 12,
    fontWeight: '600',
  },
  // Template modal styles
  csvInstructions: {
    fontSize: 14,
    color: '#666',
    marginBottom: 12,
    lineHeight: 20,
  },
  templateCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F8F9FA',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E1E5E9',
  },
  templateIcon: {
    fontSize: 32,
    marginRight: 16,
  },
  templateInfo: {
    flex: 1,
  },
  templateName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  templateCount: {
    fontSize: 14,
    color: '#666',
  },
  templateArrow: {
    fontSize: 20,
    color: '#007AFF',
  },
  // CSV import styles
  csvUploadContainer: {
    marginBottom: 20,
  },
  csvUploadButton: {
    backgroundColor: '#F8F9FA',
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#007AFF',
    borderStyle: 'dashed',
    alignItems: 'center',
    marginBottom: 12,
  },
  csvUploadButtonText: {
    color: '#007AFF',
    fontSize: 16,
    fontWeight: '600',
  },
  csvFileName: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    fontStyle: 'italic',
  },
});

export default ServiceManagementScreen;

